import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReportsService } from '../../api-client/api/reports.service';
import { LoggingService } from '../shared/logging/logging.service';
import {
    ModulesReportsApplicationReportsExecuteReportReportExecutionResponse,
    ModulesReportsEndpointsContractsReportsExecuteReportRequest,
} from '../../api-client';

@Injectable({ providedIn: 'root' })
export class ReportExecutionService {
    private readonly reportsService = inject(ReportsService);
    private readonly toastr = inject(ToastrService);
    private readonly logger = inject(LoggingService);

    async executeAndDownload(options: {
        reportId: string | number;
        format?: 'PDF' | 'TXT' | 'XLS';
        parameters?: Record<string, string | number | boolean | null | undefined>;
        context?: Record<string, string | number | boolean | null | undefined>;
        fileNameFallback?: string;
        successMessage?: string;
    }): Promise<void> {
        const {
            reportId,
            format = 'PDF',
            parameters = {},
            context = {},
            fileNameFallback,
            successMessage = 'Report downloaded successfully.',
        } = options;

        const request: ModulesReportsEndpointsContractsReportsExecuteReportRequest = {
            format,
        };

        const normalizedParameters = this.normalizePayload(parameters);
        if (normalizedParameters) {
            request.parameters = normalizedParameters;
        }

        const normalizedContext = this.normalizePayload(context);
        if (normalizedContext) {
            request.context = normalizedContext;
        }

        this.logger.info('ReportExecutionService', 'Executing report', {
            reportId,
            format,
            parameters,
            context,
        });

        try {
            const response = await firstValueFrom(
                this.reportsService.executeReportEndpoint(String(reportId), request)
            );

            const downloaded = this.downloadReportContent(
                response,
                fileNameFallback ?? this.generateDefaultFileName(reportId, format)
            );

            if (downloaded) {
                this.toastr.success(successMessage, 'Report Ready');
            }
        } catch (error) {
            this.logger.error('ReportExecutionService', 'Failed to execute report', error);
            const detail =
                (error as any)?.error?.detail ||
                (error as any)?.error?.title ||
                (error as any)?.message ||
                'Failed to generate report. Please try again.';
            this.toastr.error(detail, 'Report Failed');
            throw error;
        }
    }

    private normalizePayload(
        payload?: Record<string, string | number | boolean | null | undefined>
    ): Record<string, string> | undefined {
        if (!payload) {
            return undefined;
        }

        const entries = Object.entries(payload)
            .filter(([key, value]) => !!key && value !== null && value !== undefined)
            .map(([key, value]) => [key, String(value)] as const);

        if (entries.length === 0) {
            return undefined;
        }

        return entries.reduce<Record<string, string>>((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
    }

    private downloadReportContent(
        response: ModulesReportsApplicationReportsExecuteReportReportExecutionResponse,
        fallbackFileName: string
    ): boolean {
        if (!response?.content) {
            this.toastr.error('The report did not return any content.', 'Report Failed');
            return false;
        }

        const fileName = response.fileName?.trim() || fallbackFileName;
        const contentType = response.contentType?.trim() || this.resolveContentType(fileName);

        try {
            const byteCharacters = atob(response.content);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let index = 0; index < byteCharacters.length; index += 1) {
                byteArray[index] = byteCharacters.charCodeAt(index);
            }

            const blob = new Blob([byteArray], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            anchor.click();
            window.URL.revokeObjectURL(url);
            return true;
        } catch (error) {
            this.logger.error('ReportExecutionService', 'Failed to download report content', error);
            this.toastr.error('Unable to download the report file.', 'Download Failed');
            return false;
        }
    }

    private resolveContentType(filenameOrFormat: string): string {
        const value = filenameOrFormat.toLowerCase();
        if (value.endsWith('.txt') || value === 'txt') {
            return 'text/plain';
        }
        if (value.endsWith('.xls') || value === 'xls') {
            return 'application/vnd.ms-excel';
        }
        if (value.endsWith('.xlsx') || value === 'xlsx') {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }
        return 'application/pdf';
    }

    private generateDefaultFileName(reportId: string | number, format: string): string {
        const extension = format.toLowerCase();
        return `report-${reportId}-${Date.now()}.${extension}`;
    }
}
